// @ts-nocheck -- verbatim TAPP payroll UI compatibility boundary
import { type ButtonHTMLAttributes, forwardRef } from "react";
import { Button as HeroButton } from "@heroui/react";
import { clsx } from "clsx";
import { IconLoader2 } from "@tabler/icons-react";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "danger"
  | "warning"
  | "outline";
export type ButtonSize = "xs" | "sm" | "md" | "lg";

const variantClasses: Record<ButtonVariant, string> = {
  primary: "bg-accent hover:bg-accent-hover text-accent-fg disabled:opacity-40",
  secondary: "bg-elevated hover:bg-border text-foreground disabled:opacity-40",
  ghost:
    "bg-transparent hover:bg-elevated text-foreground-muted hover:text-foreground disabled:opacity-40",
  danger:
    "bg-danger-subtle hover:bg-danger text-danger hover:text-white disabled:opacity-40",
  warning:
    "bg-warning-subtle hover:bg-warning/20 text-warning disabled:opacity-40",
  outline:
    "bg-transparent border border-border-strong hover:border-border hover:bg-elevated text-foreground disabled:opacity-40",
};

const sizeClasses: Record<ButtonSize, string> = {
  xs: "h-7 px-2.5 text-xs rounded-md gap-1.5",
  sm: "h-8 px-3 text-sm rounded-md gap-2",
  md: "h-9 px-4 text-sm rounded-lg gap-2",
  lg: "h-11 px-5 text-base rounded-lg gap-2.5",
};

const heroVariants = {
  primary: "primary",
  secondary: "secondary",
  ghost: "ghost",
  outline: "outline",
  danger: "danger-soft",
  warning: "secondary",
} as const;

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconOnly?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      loading = false,
      iconOnly = false,
      disabled,
      className,
      children,
      ...props
    },
    ref,
  ) => (
    <HeroButton.Root
      ref={ref}
      isDisabled={disabled || loading}
      isPending={loading}
      isIconOnly={iconOnly}
      size={size === "xs" ? "sm" : size}
      variant={heroVariants[variant]}
      data-payday-variant={variant}
      aria-busy={loading || undefined}
      className={clsx(
        "payday-button inline-flex cursor-pointer items-center justify-center font-medium",
        "transition-colors duration-150",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2",
        "disabled:cursor-not-allowed",
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...props}
    >
      {loading ? (
        <span className="inline-flex items-center gap-2">
          <IconLoader2
            className="animate-spin motion-reduce:animate-none"
            size={16}
            aria-hidden="true"
          />
          {!iconOnly && <span>{children}</span>}
        </span>
      ) : (
        children
      )}
    </HeroButton.Root>
  ),
);

Button.displayName = "Button";
